import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  APIProvider,
  Map as GoogleMap,
  useMap,
} from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import {
  Building2,
  Check,
  Compass,
  Copy,
  ExternalLink,
  Filter,
  Layers,
  Mail,
  MapPin,
  MessageSquare,
  Navigation,
  Phone,
  RotateCcw,
  Search,
  TriangleAlert,
  User,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type BusinessMapLocation,
  type BusinessStatus,
  useBusinessMapLocations,
  useCities,
} from "@/hooks/useBusinesses";

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as
  | string
  | undefined;
const MAP_ID =
  (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined) ||
  "postnatalcare-admin-map";
const KERALA_CENTER = { lat: 10.8505, lng: 76.2711 };

const statusConfig: Record<
  string,
  { bg: string; border: string; badge: string; hex: string }
> = {
  APPROVED: {
    bg: "bg-emerald-600",
    border: "border-emerald-700",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    hex: "#16a34a",
  },
  PENDING: {
    bg: "bg-amber-500",
    border: "border-amber-600",
    badge: "bg-amber-50 text-amber-700 border-amber-200",
    hex: "#f59e0b",
  },
  REJECTED: {
    bg: "bg-red-600",
    border: "border-red-700",
    badge: "bg-red-50 text-red-700 border-red-200",
    hex: "#dc2626",
  },
  SUSPENDED: {
    bg: "bg-purple-600",
    border: "border-purple-700",
    badge: "bg-purple-50 text-purple-700 border-purple-200",
    hex: "#9333ea",
  },
};

const statusOptions: Array<{ value: "all" | BusinessStatus; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "APPROVED", label: "Approved" },
  { value: "PENDING", label: "Pending" },
  { value: "REJECTED", label: "Rejected" },
  { value: "SUSPENDED", label: "Suspended" },
];

const MapViewport = ({
  locations,
  selected,
}: {
  locations: BusinessMapLocation[];
  selected: BusinessMapLocation | null;
}) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !locations.length) return;
    if (selected) return;

    const bounds = new google.maps.LatLngBounds();
    locations.forEach((loc) => bounds.extend({ lat: loc.lat, lng: loc.lng }));

    if (locations.length === 1) {
      map.setCenter({ lat: locations[0].lat, lng: locations[0].lng });
      map.setZoom(14);
      return;
    }

    map.fitBounds(bounds, 60);
  }, [locations, map, selected]);

  useEffect(() => {
    if (!map || !selected) return;
    map.panTo({ lat: selected.lat, lng: selected.lng });
    const currentZoom = map.getZoom() ?? 0;
    if (currentZoom < 14) map.setZoom(15);
  }, [map, selected]);

  return null;
};

const ClusteredMarkers = ({
  locations,
  selected,
  onSelect,
}: {
  locations: BusinessMapLocation[];
  selected: BusinessMapLocation | null;
  onSelect: (location: BusinessMapLocation) => void;
}) => {
  const map = useMap();
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<
    Map<string, google.maps.marker.AdvancedMarkerElement | google.maps.Marker>
  >(new Map());

  useEffect(() => {
    if (!map) return;

    if (!clustererRef.current) {
      clustererRef.current = new MarkerClusterer({ map, markers: [] });
    }

    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!map || !clustererRef.current) return;

    clustererRef.current.clearMarkers();
    markersRef.current.clear();

    const createdMarkers: Array<
      google.maps.marker.AdvancedMarkerElement | google.maps.Marker
    > = [];

    locations.forEach((location) => {
      const isSelected = selected?.id === location.id;
      const hexColor = statusConfig[location.businessStatus]?.hex || "#64748b";

      const pinElement = document.createElement("div");
      pinElement.className = "group relative cursor-pointer select-none";
      pinElement.title = `${location.businessName} (${location.city})`;

      pinElement.innerHTML = `
        <div class="relative flex items-center justify-center transition-all duration-200 transform ${
          isSelected ? "scale-125 z-30" : "hover:scale-110 z-10"
        }">
          ${
            isSelected
              ? `<div class="absolute -inset-2 rounded-full animate-ping opacity-75" style="background-color: ${hexColor}; opacity: 0.3;"></div>`
              : ""
          }
          <div class="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white text-white shadow-lg transition-shadow duration-200 ${
            isSelected ? "ring-4 ring-primary/40 shadow-xl" : "shadow-md"
          }" style="background-color: ${hexColor};">
            <svg class="h-4.5 w-4.5" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
            </svg>
          </div>
          <div class="absolute -bottom-1.5 h-2 w-2 rotate-45 border-b border-r border-white" style="background-color: ${hexColor};"></div>
        </div>
      `;

      pinElement.addEventListener("click", (e) => {
        e.stopPropagation();
        onSelect(location);
      });

      let marker: google.maps.marker.AdvancedMarkerElement | google.maps.Marker;

      if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
        marker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position: { lat: location.lat, lng: location.lng },
          title: location.businessName,
          content: pinElement,
        });
      } else {
        marker = new google.maps.Marker({
          map,
          position: { lat: location.lat, lng: location.lng },
          title: location.businessName,
        });
        marker.addListener("click", () => onSelect(location));
      }

      markersRef.current.set(location.id, marker);
      createdMarkers.push(marker);
    });

    clustererRef.current.addMarkers(createdMarkers);
  }, [locations, selected, map, onSelect]);

  return null;
};

export const BusinessLocationsPage = () => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedCity, setSelectedCity] = useState<string>("all");
  const [status, setStatus] = useState<"all" | BusinessStatus>("all");
  const [typeFilter, setTypeFilter] = useState<"all" | "BUSINESS" | "BRANCH">(
    "all",
  );
  const [selected, setSelected] = useState<BusinessMapLocation | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const sidebarContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput), 250);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const { data: citiesList } = useCities();

  const filters = useMemo(
    () => ({
      search: search || undefined,
      city: selectedCity === "all" ? undefined : selectedCity,
      status: status === "all" ? undefined : status,
    }),
    [selectedCity, search, status],
  );

  const { data, isLoading, error, refetch } = useBusinessMapLocations(filters);

  const allLocations = useMemo(() => data?.locations ?? [], [data]);

  const locations = useMemo(() => {
    if (typeFilter === "all") return allLocations;
    return allLocations.filter((loc) => loc.type === typeFilter);
  }, [allLocations, typeFilter]);

  const stats = useMemo(() => {
    const total = allLocations.length;
    const approved = allLocations.filter(
      (l) => l.businessStatus === "APPROVED",
    ).length;
    const pending = allLocations.filter(
      (l) => l.businessStatus === "PENDING",
    ).length;
    const rejected = allLocations.filter(
      (l) => l.businessStatus === "REJECTED",
    ).length;
    const suspended = allLocations.filter(
      (l) => l.businessStatus === "SUSPENDED",
    ).length;
    return { total, approved, pending, rejected, suspended };
  }, [allLocations]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (search) count++;
    if (selectedCity !== "all") count++;
    if (status !== "all") count++;
    if (typeFilter !== "all") count++;
    return count;
  }, [search, selectedCity, status, typeFilter]);

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setSelectedCity("all");
    setStatus("all");
    setTypeFilter("all");
  };

  const handleSelectLocation = (location: BusinessMapLocation) => {
    setSelected(location);
    const element = document.getElementById(`sidebar-item-${location.id}`);
    if (element && sidebarContainerRef.current) {
      element.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  const copyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(`${fieldName}-${text}`);
    toast.success(`${fieldName} copied to clipboard`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  useEffect(() => {
    if (selected && !locations.some((loc) => loc.id === selected.id)) {
      setSelected(null);
    }
  }, [locations, selected]);

  return (
    <AppLayout title="Eagle View Map">
      <div className="mb-4 space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-foreground">
                Eagle View Map
              </h1>
              <Badge
                variant="outline"
                className="bg-primary/5 text-primary border-primary/20"
              >
                {locations.length}{" "}
                {locations.length === 1 ? "Location" : "Locations"}
              </Badge>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Interactive map overview of all registered business centres and
              branch locations
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground shadow-xs">
              <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
              <span>{stats.approved} Approved</span>
            </div>
            <div className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground shadow-xs">
              <span className="h-2 w-2 rounded-full bg-amber-500"></span>
              <span>{stats.pending} Pending</span>
            </div>
            {stats.rejected > 0 && (
              <div className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground shadow-xs">
                <span className="h-2 w-2 rounded-full bg-red-500"></span>
                <span>{stats.rejected} Rejected</span>
              </div>
            )}

            {data && data.missingCoordinates > 0 && (
              <div className="flex items-center gap-1.5 rounded-md border border-warning/30 bg-warning/10 px-2.5 py-1 text-xs font-medium text-warning">
                <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
                <span>{data.missingCoordinates} unmapped</span>
              </div>
            )}
          </div>
        </div>

        {/* Filter Controls Bar */}
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-2.5 shadow-xs">
          <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search business, city, address or contact…"
              className="h-8.5 pl-8 text-xs"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <Select
            value={status}
            onValueChange={(val) => setStatus(val as "all" | BusinessStatus)}
          >
            <SelectTrigger className="h-8.5 w-[140px] text-xs">
              <div className="flex items-center gap-1.5 truncate">
                <Filter className="h-3 w-3 text-muted-foreground shrink-0" />
                <SelectValue placeholder="Status" />
              </div>
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  className="text-xs"
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* City Filter */}
          <Select value={selectedCity} onValueChange={setSelectedCity}>
            <SelectTrigger className="h-8.5 w-[140px] text-xs">
              <div className="flex items-center gap-1.5 truncate">
                <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
                <SelectValue placeholder="City" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                All Cities
              </SelectItem>
              {citiesList?.map((city) => (
                <SelectItem key={city} value={city} className="text-xs">
                  {city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Type Filter */}
          <Select
            value={typeFilter}
            onValueChange={(val) =>
              setTypeFilter(val as "all" | "BUSINESS" | "BRANCH")
            }
          >
            <SelectTrigger className="h-8.5 w-[140px] text-xs">
              <div className="flex items-center gap-1.5 truncate">
                <Layers className="h-3 w-3 text-muted-foreground shrink-0" />
                <SelectValue placeholder="Type" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                All Location Types
              </SelectItem>
              <SelectItem value="BUSINESS" className="text-xs">
                Main Businesses
              </SelectItem>
              <SelectItem value="BRANCH" className="text-xs">
                Branches Only
              </SelectItem>
            </SelectContent>
          </Select>

          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="h-8.5 px-2.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="mr-1 h-3 w-3" />
              Reset ({activeFilterCount})
            </Button>
          )}
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-sm">
          <p className="font-semibold text-destructive">
            Unable to load business map locations.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Check database connection or network permissions.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            className="mt-3 text-xs"
          >
            Try Again
          </Button>
        </div>
      ) : (
        <div className="grid h-[calc(100vh-215px)] min-h-[550px] overflow-hidden rounded-xl border border-border bg-card shadow-sm xl:grid-cols-[24rem_minmax(0,1fr)]">
          {/* Sidebar Business List */}
          <aside
            ref={sidebarContainerRef}
            className="flex flex-col border-b border-border bg-card xl:border-b-0 xl:border-r max-h-[380px] xl:max-h-none overflow-hidden"
          >
            <div className="flex items-center justify-between border-b border-border bg-muted/40 px-4 py-2.5">
              <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                Registered Businesses ({locations.length})
              </span>
              {selected && (
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
                >
                  Clear Selection
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-border">
              {isLoading ? (
                <div className="space-y-3 p-4">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <div key={n} className="animate-pulse space-y-2">
                      <div className="h-4 w-3/4 rounded bg-muted"></div>
                      <div className="h-3 w-1/2 rounded bg-muted/60"></div>
                      <div className="h-3 w-5/6 rounded bg-muted/40"></div>
                    </div>
                  ))}
                </div>
              ) : locations.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-10 text-center text-muted-foreground">
                  <Building2 className="mb-2 h-8 w-8 text-muted-foreground/50" />
                  <p className="text-sm font-medium">No matching locations</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Try refining your search text or resetting active filters.
                  </p>
                  {activeFilterCount > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={resetFilters}
                      className="mt-3 text-xs"
                    >
                      Reset Filters
                    </Button>
                  )}
                </div>
              ) : (
                locations.map((location) => {
                  const isSelected = selected?.id === location.id;

                  return (
                    <div
                      key={location.id}
                      id={`sidebar-item-${location.id}`}
                      onClick={() => handleSelectLocation(location)}
                      className={`group relative cursor-pointer p-3.5 transition-all ${
                        isSelected
                          ? "bg-primary/10 border-l-4 border-primary ring-1 ring-primary/20 shadow-xs"
                          : "hover:bg-muted/50 border-l-4 border-transparent"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="truncate text-sm font-bold text-foreground group-hover:text-primary transition-colors">
                              {location.businessName}
                            </p>
                            {location.type === "BRANCH" && (
                              <Badge
                                variant="secondary"
                                className="px-1.5 py-0 text-[10px] font-normal shrink-0"
                              >
                                Branch
                              </Badge>
                            )}
                          </div>
                          <p className="mt-0.5 truncate text-xs font-medium text-muted-foreground">
                            {location.branchName
                              ? `${location.branchName}`
                              : "Main Location"}
                            {location.isPrimary ? " · Primary" : ""}
                          </p>
                        </div>
                        <StatusBadge
                          status={location.businessStatus}
                          className="shrink-0"
                        />
                      </div>

                      {/* Location Address */}
                      <div className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
                        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                        <span className="line-clamp-2 leading-relaxed">
                          {location.address},{" "}
                          <strong className="font-semibold text-foreground/80">
                            {location.city}
                          </strong>
                        </span>
                      </div>

                      {/* Contact Info Snippet */}
                      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border/60 pt-2 text-[11px] text-muted-foreground">
                        {location.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3 shrink-0 text-muted-foreground" />
                            <a
                              href={`tel:${location.phone}`}
                              onClick={(e) => e.stopPropagation()}
                              className="hover:text-primary hover:underline font-mono"
                            >
                              {location.phone}
                            </a>
                          </div>
                        )}

                        {location.email && (
                          <div className="flex items-center gap-1 min-w-0 max-w-[170px]">
                            <Mail className="h-3 w-3 shrink-0 text-muted-foreground" />
                            <a
                              href={`mailto:${location.email}`}
                              onClick={(e) => e.stopPropagation()}
                              className="truncate hover:text-primary hover:underline"
                              title={location.email}
                            >
                              {location.email}
                            </a>
                          </div>
                        )}

                        {location.ownerName && (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 shrink-0 text-muted-foreground" />
                            <span className="truncate max-w-[120px]">
                              {location.ownerName}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="mt-2.5 flex items-center justify-between pt-1">
                        <span className="text-[10px] text-muted-foreground">
                          {location.isBusinessVisible
                            ? "Visible to customers"
                            : "Hidden from app"}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectLocation(location);
                            }}
                            className="inline-flex items-center text-[11px] font-semibold text-primary hover:underline"
                          >
                            <Compass className="mr-1 h-3 w-3" /> Focus
                          </button>
                          <Link
                            to={`/businesses/${location.businessId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center text-[11px] font-medium text-muted-foreground hover:text-foreground"
                          >
                            <ExternalLink className="mr-1 h-3 w-3" /> Open
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </aside>

          {/* Interactive Map Section */}
          <div className="relative flex flex-col min-h-[400px] xl:min-h-0 bg-muted/20">
            {!GOOGLE_MAPS_API_KEY ? (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-card">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-3">
                  <Navigation className="h-6 w-6" />
                </div>
                <h3 className="text-base font-semibold text-foreground">
                  Interactive Google Maps Setup Required
                </h3>
                <p className="mt-1 max-w-md text-xs text-muted-foreground leading-relaxed">
                  Configure{" "}
                  <code className="px-1.5 py-0.5 rounded bg-muted text-foreground font-mono text-[11px]">
                    VITE_GOOGLE_MAPS_API_KEY
                  </code>{" "}
                  in{" "}
                  <code className="px-1.5 py-0.5 rounded bg-muted font-mono text-[11px]">
                    .env
                  </code>{" "}
                  to render live interactive satellite and clustered map
                  markers.
                </p>
                <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4 max-w-md text-left text-xs space-y-1.5">
                  <p className="font-semibold text-foreground">
                    Available location features without API key:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li>Full business and branch search and filtering</li>
                    <li>Detailed contact information & address lookups</li>
                    <li>
                      Coordinates and direct links to external Google Maps
                    </li>
                  </ul>
                </div>
              </div>
            ) : (
              <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                <GoogleMap
                  defaultCenter={KERALA_CENTER}
                  defaultZoom={7}
                  mapId={MAP_ID}
                  gestureHandling="greedy"
                  disableDefaultUI={false}
                  style={{ width: "100%", height: "100%" }}
                >
                  <MapViewport locations={locations} selected={selected} />
                  <ClusteredMarkers
                    locations={locations}
                    selected={selected}
                    onSelect={handleSelectLocation}
                  />
                </GoogleMap>
              </APIProvider>
            )}

            {/* Selected Location Overlay Card */}
            {selected && (
              <div className="absolute bottom-4 left-4 right-4 z-20 rounded-xl border border-border bg-card/95 p-4 shadow-xl backdrop-blur-md transition-all sm:right-auto sm:w-96 animate-in fade-in slide-in-from-bottom-2">
                <div className="flex items-start justify-between gap-3 border-b border-border/80 pb-2.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h4 className="font-bold text-sm text-foreground truncate">
                        {selected.businessName}
                      </h4>
                      {selected.type === "BRANCH" && (
                        <Badge
                          variant="secondary"
                          className="px-1.5 py-0 text-[10px] shrink-0"
                        >
                          Branch
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {selected.branchName || "Main Location"}{" "}
                      {selected.isPrimary ? "· Primary Branch" : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <StatusBadge status={selected.businessStatus} />
                    <button
                      type="button"
                      onClick={() => setSelected(null)}
                      className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                      aria-label="Close popup"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Address */}
                <div className="mt-2.5 flex items-start gap-1.5 text-xs text-muted-foreground">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 text-primary shrink-0" />
                  <span className="leading-normal">
                    {selected.address},{" "}
                    <strong className="text-foreground">{selected.city}</strong>
                  </span>
                </div>

                {/* Contact Information Grid */}
                <div className="mt-3 grid grid-cols-1 gap-2 rounded-lg bg-muted/40 p-2.5 text-xs border border-border/50">
                  {selected.phone && (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 truncate">
                        <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="font-mono text-foreground">
                          {selected.phone}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() =>
                            copyToClipboard(selected.phone!, "Phone")
                          }
                          className="p-1 text-muted-foreground hover:text-primary transition-colors"
                          title="Copy phone"
                        >
                          {copiedField === `Phone-${selected.phone}` ? (
                            <Check className="h-3.5 w-3.5 text-emerald-600" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <a
                          href={`tel:${selected.phone}`}
                          className="rounded bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary hover:bg-primary/20"
                        >
                          Call
                        </a>
                      </div>
                    </div>
                  )}

                  {selected.email && (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 truncate min-w-0">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate text-foreground">
                          {selected.email}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() =>
                            copyToClipboard(selected.email!, "Email")
                          }
                          className="p-1 text-muted-foreground hover:text-primary transition-colors"
                          title="Copy email"
                        >
                          {copiedField === `Email-${selected.email}` ? (
                            <Check className="h-3.5 w-3.5 text-emerald-600" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <a
                          href={`mailto:${selected.email}`}
                          className="rounded bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-muted/80"
                        >
                          Email
                        </a>
                      </div>
                    </div>
                  )}

                  {selected.whatsapp && (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 truncate font-mono">
                        <MessageSquare className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                        <span className="text-foreground">
                          {selected.whatsapp}
                        </span>
                      </div>
                      <a
                        href={`https://wa.me/${selected.whatsapp.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-200"
                      >
                        WhatsApp
                      </a>
                    </div>
                  )}

                  {selected.ownerName && (
                    <div className="flex items-center gap-1.5 text-muted-foreground pt-1 border-t border-border/40">
                      <User className="h-3.5 w-3.5 shrink-0" />
                      <span>
                        Owner:{" "}
                        <strong className="text-foreground font-medium">
                          {selected.ownerName}
                        </strong>
                      </span>
                    </div>
                  )}
                </div>

                {/* External directions link & Open details button */}
                <div className="mt-3 flex items-center justify-between gap-2 pt-1">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${selected.lat},${selected.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center text-xs font-medium text-muted-foreground hover:text-foreground"
                  >
                    <Navigation className="mr-1 h-3.5 w-3.5 text-primary" />{" "}
                    Directions
                  </a>
                  <Link
                    to={`/businesses/${selected.businessId}`}
                    className="inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground shadow-xs hover:bg-primary/90 transition-colors"
                  >
                    Open Business Page{" "}
                    <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default BusinessLocationsPage;
